/**
 * EN-1224: tells whether a failed request was interrupted on the client side, as opposed to the
 * server being slow. Production data showed most `account/resolved` timeouts never reached the
 * API server at all: the machine slept or the browser froze the tab mid-request, or the browser
 * was offline. Those failures are not backend latency and must not be reported as such.
 *
 * Deliberately per-request and stateless. Tab visibility is not a signal: hidden tabs keep
 * fetching normally, so a hidden tab polling a down backend must still report backend_latency.
 */
export type ClientInterruption = "offline" | "suspended";

// ky arms one non-repeating setTimeout per request. Background-tab throttling only delays such a
// timer by about a second; sleep or a frozen tab pauses it for as long as they last, while the
// wall clock keeps running. A slow or dead server can't push a failure past the timeout, since
// ky aborts there.
const SUSPEND_SLACK_MS = 10_000;

/**
 * Why a request started at `startedAt` (epoch ms) with ky timeout `timeoutMs` was interrupted
 * client-side, or null if nothing shows it was.
 */
export function getClientInterruption(startedAt: number, timeoutMs: number | false): ClientInterruption | null {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return "offline";
    }
    if (timeoutMs !== false && Date.now() - startedAt > timeoutMs + SUSPEND_SLACK_MS) {
        return "suspended";
    }
    return null;
}
